import { TransactionItemFooter } from '@/features/transaction_items/components/Drawer/Footer/Footer';
import { TransactionItemHeader } from '@/features/transaction_items/components/Drawer/Header/Header';
import { useCancelConfirm } from '@/features/transaction_items/components/Drawer/hooks/useCancelConfirm';
import { useCheckIsSupportedOffice } from '@/features/transaction_items/List/Table/hooks/useCheckIsSupportedOffice';
import {
  TransactionItemDrawerMode,
  TransactionItemDrawerModeEnum,
  TransactionItemsColumn,
} from '@/features/transaction_items/List/type';
import { Drawer } from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback } from 'react';

export type Props = {
  visible: boolean;
  onClose: () => void;
  mode: TransactionItemDrawerMode;
  type?: TransactionItemsColumn['type'];
  children: React.ReactNode;
  isLoading: boolean;
  onSetMode: (mode: TransactionItemDrawerMode) => void;
  isDetailLoading: boolean;
  isShowUpdateConfirm: boolean;
  onDeleteClick: () => void;
  canDelete?: boolean;
  deleteTooltip?: string;
  isSubmitDisabled?: boolean;
};

export const TransactionItemDrawer: FC<Props> = memo(
  ({
    visible,
    onClose,
    mode,
    type,
    children,
    isLoading,
    onSetMode,
    isDetailLoading,
    isShowUpdateConfirm,
    onDeleteClick,
    canDelete,
    deleteTooltip,
    isSubmitDisabled = false,
  }) => {
    const { confirm, ConfirmModal } = useCancelConfirm();

    const isSupportedOffice = useCheckIsSupportedOffice();

    const onCloseDrawer = useCallback(async () => {
      if (mode === TransactionItemDrawerModeEnum.EDIT && isShowUpdateConfirm) {
        const isConfirmed = await confirm();
        if (isConfirmed) {
          onClose();
        }
      } else {
        onClose();
      }
    }, [mode, onClose, confirm, isShowUpdateConfirm]);
    return (
      <>
        <Drawer
          visible={visible}
          onClose={onCloseDrawer}
          footer={
            mode !== TransactionItemDrawerModeEnum.DETAIL && (
              <TransactionItemFooter
                mode={mode}
                onClose={onCloseDrawer}
                isLoading={isLoading}
                isSubmitDisabled={isSubmitDisabled}
              />
            )
          }
          title={
            <TransactionItemHeader
              isLoading={isDetailLoading}
              mode={mode}
              type={type}
              onSetMode={onSetMode}
              onDeleteClick={onDeleteClick}
              canDelete={canDelete}
              deleteTooltip={deleteTooltip}
              isSupportedOffice={isSupportedOffice}
            />
          }
          mask
          maskClosable={false}
        >
          {children}
        </Drawer>
        {mode === TransactionItemDrawerModeEnum.EDIT && <ConfirmModal />}
      </>
    );
  }
);

TransactionItemDrawer.displayName = 'TransactionItemDrawer';
